import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from "@angular/common/http";
import { inject, Injector, Service } from "@angular/core";
import { environment } from "environments/environment";
import { Observable, throwError } from "rxjs";

import { AuthService } from "./auth.service";
import { catchError, switchMap } from "rxjs/operators";
import { B2CPolicies } from "../models/auth-config.model";
import { MsalService } from "@azure/msal-angular";
import { Router } from "@angular/router";
import { UtilsService } from "app/back-office/shared/services/core/utils.service";
import { SessionStore } from "app/shared/state/session.store";

@Service()
export class AuthInterceptor implements HttpInterceptor {
  private msalService = inject(MsalService);
  private authService = inject(AuthService);
  private _utils = inject(UtilsService);
  private _router = inject(Router);
  private injector = inject(Injector);

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const props = Object.keys(environment.urls);
    if (
      props.some(
        (p) =>
          !!environment.urls[p].authRequired &&
          !!request?.url?.includes(environment.urls[p].url),
      )
    ) {
      const token: string = this.authService.tokenValue;
      token
        ? (request = this.addTokenHeader(request, token))
        : console.error("No token");
    }

    const session = this.injector.get(SessionStore);
    const tenantId = session.userInfo()?.tenant?.id;

    if (tenantId) {
      request = request.clone({
        setHeaders: { __tenant: tenantId },
      });
    }

    if (request.headers.has("skipErrorHandling")) {
      const newHeaders = request.headers.delete("skipErrorHandling");
      const cleanReq = request.clone({ headers: newHeaders });
      return next.handle(cleanReq);
    }

    return next.handle(request).pipe(
      catchError((error) => {
        return this.handleHttpError(
          request,
          next,
          error as HttpErrorResponse,
          request.method,
        );
      }),
    );
  }

  private addTokenHeader(
    request: HttpRequest<any>,
    token: string,
  ): HttpRequest<any> {
    return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
  }

  private handleHttpError(
    request: HttpRequest<any>,
    next: HttpHandler,
    error: HttpErrorResponse,
    method?: string,
  ): Observable<HttpEvent<any>> {
    switch (error.status) {
      case 401:
        return this.handle401Error(request, next);
      case 400:
        this.openErrorToast(
          error.status,
          error?.error?.error?.message || "Request not valid",
          error?.error?.error?.details,
          method,
          error.url,
        );
        throw error;
      case 403:
        this.openErrorToast(
          error.status,
          error?.error?.error?.message || "Forbidden",
          error?.error?.error?.details,
          method,
          error.url,
        );
        throw error;
      case 404:
        this.openErrorToast(
          error.status,
          error?.error?.error?.message || "Not Found",
          error?.error?.error?.details,
          method,
          error.url,
        );
        throw error;
      case 405:
        this.openErrorToast(
          error.status,
          error?.error?.error?.message || "Method Not Allowed",
          error?.error?.error?.details,
          method,
          error.url,
        );
        throw error;
      case 500:
        this.openErrorToast(
          error.status,
          error?.error?.error?.message || "Internal Server Error",
          error?.error?.error?.details,
          method,
          error.url,
        );
        throw error;
      case 501:
        this.openErrorToast(
          error.status,
          error?.error?.error?.message || "Not Implemented",
          error?.error?.error?.details,
          method,
          error.url,
        );
        throw error;
      case 503:
        this.openErrorToast(
          error.status,
          error?.error?.error?.message || "Service Unavailable",
          error?.error?.error?.details,
          method,
          error.url,
        );
        throw error;
      default:
        this.openErrorToast(
          error.status,
          error?.error?.error?.message || "An error occurred",
          error?.error?.error?.details,
          method,
          error.url,
        );
        throw error;
    }
  }

  private openErrorToast(
    status: number,
    message: string,
    detail: string,
    method?: string,
    url?: string,
  ) {
    const messageForSnack = `
      ${method ? `<strong>Method:</strong> ${method.toUpperCase()}` : ""}
      ${url ? `<br><strong>Url:</strong> ${url}` : ""}
      <br><strong>Status:</strong> ${status}
      <br><strong>Error message:</strong> ${message}
      ${detail ? `<br><pre>${detail}</pre>` : ""}
      `;

    this._utils.showSnackbar(messageForSnack, null, "danger", null, true);
  }

  /**
   * Refresh silenzioso del token e replay della richiesta.
   *
   * OGNI ramo deve restituire uno stream, e un fallimento deve PROPAGARE un errore. Prima qui si
   * tornava `of(null)`: dentro la pipeline di `HttpClient`, che con `observe: 'body'` filtra tutto
   * cio' che non e' `HttpResponse`, quel `null` veniva scartato e la sorgente completava a vuoto.
   * Il chiamante non riceveva ne' dato ne' errore e restava appeso per sempre, bruciando una
   * connessione (dalla settima Chrome mette tutto in Stalled). Vedi
   * docs/features/returns/plans/2026-09-11-picker-richieste-appese.md
   */
  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const account = this.msalService.instance.getAllAccounts()[0];
    return this.msalService
      .acquireTokenSilent({
        account: account,
        scopes: [B2CPolicies().scope],
        redirectUri: window.location.href,
      })
      .pipe(
        catchError((error) => {
          this.authService.logout();
          return throwError(() => error);
        }),
        switchMap((token) => {
          const accessToken = token?.accessToken;
          if (!accessToken) {
            this._router.navigateByUrl("errors/unauthorized");
            return throwError(
              () => new Error("Silent token refresh returned no access token"),
            );
          }
          this.authService.setToken(accessToken);
          return next
            .handle(this.addTokenHeader(request, accessToken))
            .pipe(
              catchError((retryError) => {
                this.authService.logout();
                return throwError(() => retryError);
              }),
            );
        }),
      );
  }
}
